import React, {useEffect, useState} from 'react';
import {Box, Button, Checkbox, CircularProgress, Container, styled, TextField, Card} from '@mui/material';
import Typography from '@mui/material/Typography';
import {useParams} from 'react-router-dom';
import {FileDownload, UploadFile} from '@mui/icons-material';
import {SEN_STATUSES} from '../../const/SENStatus';
import DocumentList from '../../Components/DocumentList';
import {downloadProfileEhcpDocument, downloadProfileSenDocument,} from '../../../../../api/services/filesService';
import useFileDownloader from '../../../../../utility/hooks/useFileDownloader';
import {useGetSenNeedsQuery, useManageSenNeedsMutation} from '../../../../../api/services/studentService';
import {useDispatch, useSelector} from 'react-redux';
import {showSnackbar} from '../../../../../AppLayout/ApplicationSlice';
import {retrieveProfile} from '../../ProfilesSlice';
import ConfirmationWrapper from '../../Components/ConfirmationWrapper';
import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import PropTypes from 'prop-types';
import AdmicityDialog from '../../../../../shared-components/AdmicityDialog';
import IconButton from '@mui/material/IconButton';
import Uploader from '../../../../SchoolManagement/Setup/Uploader';
import Tooltip from '@mui/material/Tooltip';
import {
    DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS,
    PROFILE_SECTIONS_KEYS
} from '../../../../../constants/ProfileSectionsKeys';
import {useGetProfileDescriptionQuery} from '../../../../../api/services/schoolManagementService';
import formatProfileDescription from '../../../../../utility/profileDescriptionFormatter';
import {getHisHerPronoun} from '../../../../../utility/pronounUtil';
import {canConfirmEhcpSenStatus} from './senEhcpUploadPolicy';
import {isSenInvestigatingUnchanged} from './senInvestigatingDirtyCheck';
import useUser from '../../../../../utility/hooks/useUser';
import UpdateRequestDialog, {UPDATE_REQUEST_BUTTON} from '../../Components/UpdateRequestDialog';

const SenFields = styled(Container)({
    display: 'flex', flexDirection: 'column', justifyContent: 'start', padding: 0
});

// Maria, 18 October 2026: each tick before its text
const SenRow = styled(Box)({
    display: 'flex', flexDirection: 'row-reverse', justifyContent: 'flex-end', alignItems: 'center', gap: 8
});

const DownloadIcon = styled(FileDownload)(({theme}) => ({
    color: theme.palette.primary.main, cursor: 'pointer'
}));

const SenAndDisabilities = ({readonly}) => {
    const [senRows, setSenRows] = useState([]);
    const [senDocumentRows, setSenDocumentRows] = useState([]);
    const [senInvestigating, setSenInvestigating] = useState({});
    const {studentId} = useParams();
    const [isAddMode, setIsAddMode] = useState(false);
    const [newFile, setNewFile] = useState(null);
    const [newDocuments, setNewDocuments] = useState([]);
    const [deletedDocuments, setDeletedDocuments] = useState([]);
    const [newFileSummary, setNewFileSummary] = useState('');
    const [senStatus, setSenStatus] = useState(undefined);
    const {selectedProfile, currentSchool} = useSelector(state => state.profilesInfo);
    const dispatch = useDispatch();
    const [ehcpFile, setEhcpFile] = useState(null);
    const [ehcpFileDeleted, setEhcpFileDeleted] = useState(false);
    const [selectedEhcpFile, setSelectedEhcpFile] = useState([]);
    const [openAddDocumentsDialog, setOpenAddDocumentsDialog] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isUploadSizeExceedsLimit, setIsUploadSizeExceedsLimit] = useState(false);
    const {
        data: senNeeds = {options: []},
        isLoading: isSenNeedsLoading,
        refetch: refetchSenNeedsData
    } = useGetSenNeedsQuery(studentId);
    const [updateSenNeeds] = useManageSenNeedsMutation();
    // AD-2827 (Maria, 29 October 2026): a parent with View access has their own introduction, a hover on Confirm and a
    // button to tell the school that the information needs to be updated
    const {user} = useUser();
    const isViewOnlyParent = Boolean(readonly && user?.isParent?.());
    const [openUpdateRequest, setOpenUpdateRequest] = useState(false);
    const {data: senViewDescriptionData} = useGetProfileDescriptionQuery(
        PROFILE_SECTIONS_KEYS.senViewAccess,
        {skip: !isViewOnlyParent}
    );
    const downloadFile = useFileDownloader();
    const {
        data: senDescriptionData
    } = useGetProfileDescriptionQuery(
        PROFILE_SECTIONS_KEYS.sen,
        {skip: !(senStatus === SEN_STATUSES.KS || senStatus === SEN_STATUSES.K)}
    );
    const senDescription = senDescriptionData?.value ?? DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.sen];

    useEffect(() => {
        if (!isSenNeedsLoading) {
            setSenRows(senNeeds.senNeedsData);
            setSenDocumentRows(senNeeds.senDocuments);
            setSenInvestigating(senNeeds.senNeedInvestigating);
            setSenStatus(Object.values(SEN_STATUSES).includes(senNeeds.senStatus) ? senNeeds.senStatus : null);
            setEhcpFile(senNeeds.ehcpFile);
            setEhcpFileDeleted(false);
        }
    }, [senNeeds, senNeeds.senNeedsData]);

    const capitalise = text => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

    const getHeShePronoun = () => {
        if (!selectedProfile.gender) {
            return 'he/she';
        }
        return selectedProfile.gender === 'M' ? 'he' : 'she';
    }

    const addFile = (file) => {
        if (!file) return;

        const fileSizeInMB = file.size / (1024 * 1024);

        if (fileSizeInMB > 15) {
            dispatch(showSnackbar({message: 'The total size of the file exceeds 15 MB.', severity: 'error'}));
        } else {
            setNewFile(file);
            setIsAddMode(true);
        }
    };

    const addNewFile = () => {
        setSenDocumentRows([{name: newFile.name, summary: newFileSummary, isNew: true}, ...senDocumentRows]);
        setNewDocuments([{summary: newFileSummary, file: newFile}, ...newDocuments]);
        setNewFile(null);
        setIsAddMode(false);
        setNewFileSummary('');
    };

    const deleteDocument = (document) => {
        if (document.id) {
            setDeletedDocuments([document.id, ...deletedDocuments]);
            setSenDocumentRows(senDocumentRows.filter(d => d.id !== document.id));
        } else {
            setNewDocuments(newDocuments.filter(d => d.file.name !== document.name));
            setSenDocumentRows(senDocumentRows.filter(d => d.name !== document.name));
        }
    };

    const cancelCreating = () => {
        setNewFile(null);
        setIsAddMode(false);
        setNewFileSummary('');
    };

    const addNewFileComponent = () => <>
        {!isAddMode && <Button
            sx={{
            backgroundColor: '#186BBE',
            boxShadow: 'none',
            width: { xs: '100%', md: 200 },
            '&:hover': {
                backgroundColor: '#155FA7',
                boxShadow: 'none'
            }
        }} component="label" variant="contained" startIcon={<UploadFile/>}>
            Add document
            <input type="file" hidden onChange={event => addFile(event.target.files[0])}/>
        </Button>}
        {newFile && isAddMode &&
            <Box>
                <Typography fontWeight="bold">{newFile.name}</Typography>
                <Typography variant="body1" py={1}>Summary*</Typography>
                <TextField multiline sx={{width: '100%'}} value={newFileSummary}
                           onChange={(e) => setNewFileSummary(e.target.value)}/>
                <Box width={'100%'} display={'flex'} justifyContent={'end'} gap={1} py={1}>
                    <Button variant="outlined" onClick={() => cancelCreating()} style={{width: '100px'}}>
                        Cancel
                    </Button>
                    <Button disabled={!newFileSummary.trim()} variant="contained" onClick={() => addNewFile()}
                            sx={{
                                backgroundColor: '#186BBE',
                                boxShadow: 'none',
                                width: 100,
                                '&:hover': {
                                    backgroundColor: '#155FA7',
                                    boxShadow: 'none'
                                }
                            }}>
                        Add
                    </Button>
                </Box>

            </Box>
        }
    </>;

    const updateSen = async ({note, isActive, id}) => {
        let sens = JSON.parse(JSON.stringify(senRows));
        let updatedSen = sens.find(row => row.id === id);
        updatedSen.isEdited = true;
        if (updatedSen.senNeedData) {

            if (note !== undefined) {
                updatedSen.senNeedData.note = note;
            }
            if (isActive) {
                updatedSen.senNeedData.isActive = isActive;
            } else if (isActive !== undefined && !isActive) {
                if (senNeeds.senNeedsData.find(e => e.id === id).senNeedData) {
                    updatedSen.senNeedData.isActive = false;
                } else {
                    delete updatedSen.isEdited;
                    updatedSen.senNeedData = null;
                }
            }
        } else {
            updatedSen.senNeedData = {note, isActive: true};
        }
        setSenRows(sens);
    };

    // Ticking another answer while 'being assessed' is ticked also clears that answer's summary
    const chooseSenStatus = (status) => {
        if (status && senInvestigating?.isChecked) {
            setSenInvestigating({isChecked: false});
        }
        setSenStatus(status);
    };

    const handleCheckInInvestigating = (checked) => {
        setSenInvestigating(checked ? {note: '', isChecked: true, isNew: true} : {isChecked: false});
        setSenStatus(checked ? SEN_STATUSES.KI : null);
    };

    const handleChangeInInvestigatingComment = (value) => {
        let updatedSenInvestigating = JSON.parse(JSON.stringify(senInvestigating));
        updatedSenInvestigating.note = value;
        updatedSenInvestigating.isEdited = true;
        setSenInvestigating(updatedSenInvestigating);
    };

    const documentsMap = [
        {
            documents: senDocumentRows,
            allowDelete: true,
            onDownload: async documentId => await downloadFile(downloadProfileSenDocument, {documentId, studentId}),
            onFetch: documentId => downloadProfileSenDocument({documentId, studentId}),
            onDelete: deleteDocument
        }
    ];

    const handleSave = async () => {
        const updatedRows = senRows
            .filter(row => row.isEdited)
            .map(row => {
                return {
                    name: row.name,
                    senNeedId: row.id,
                    ...row.senNeedData
                };
            });

        const senNeedInvestigating = {
            isChecked: senInvestigating.isChecked,
            senInvestigatingId: senInvestigating.id,
            note: senInvestigating.note
        };

        await updateSenNeeds({
            studentId,
            senNeeds: updatedRows,
            senStatus,
            senNeedInvestigating,
            addedSenDocuments: newDocuments,
            deletedSenDocuments: deletedDocuments,
            ehcpFile: ehcpFile instanceof File ? ehcpFile : null,
            deleteEhcpDocument: ehcpFileDeleted
        }).then(() => dispatch(retrieveProfile({studentId: selectedProfile.id})));

        setNewDocuments([]);
        setDeletedDocuments([]);
        setEhcpFileDeleted(false);
    };

    const isDisabledSenChecked = () => senRows?.some(senNeed => senNeed.isDisabled && senNeed.senNeedData && senNeed.senNeedData.isActive);

    // Maria, 18 October 2026: no 'SEN Documents' heading; the Add document button and the Save button on one row
    const buildSenDocuments = (actions) =>
        <Box
            display="flex"
            flexDirection="column"
            gap={2}
            marginTop={2}
        >
            <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2, '& .action-confirm': {mt: 0, alignItems: 'flex-start'}, '& .action-confirm > :first-of-type': {justifyContent: 'flex-start'}}}>
                {!readonly && <Box sx={{flex: isAddMode ? '1 1 100%' : '0 0 auto'}}>{addNewFileComponent()}</Box>}
                {actions}
            </Box>
            <DocumentList
                readonly={readonly}
                documents={documentsMap}
                disablePaddings
            />
        </Box>

    const buildSenInInvestigatingRow =
        <Box key={senInvestigating?.isChecked}>
            <SenRow>
                <Typography sx={{color: senStatus && senStatus !== SEN_STATUSES.KI ? '#9aa5b1' : 'inherit'}}>{selectedProfile.name} is in the process of being assessed for Special
                    Educational
                    Needs and may require support at school depending on the outcome of the assessment.</Typography>
                <Checkbox
                    sx={{width: '25px', height: '25px'}}
                    checked={senInvestigating?.isChecked}
                    onChange={event => handleCheckInInvestigating(event.target.checked)}
                    disabled={readonly}
                />
            </SenRow>
        </Box>;

    // Maria, 18 October 2026: the conditions in columns; a ticked condition and its description across the full width
    // Maria, 18 October 2026: the needs as one outlined table of ticks, as on Medical; a ticked need's
    // description box underneath it, the width of its column
    const visibleSenRows = () => senRows?.filter(senNeed => !senNeed.isDisabled || senNeed.senNeedData?.isActive) ?? [];
    const buildSenRows = () => (
        <Card variant="outlined" sx={{mt: 1, borderRadius: 2, display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))'}}}>
        {visibleSenRows().map((senNeed) => (
            <Box
                key={senNeed.id ?? senNeed.name}
                sx={{px: 2, py: 0.75, minWidth: 0, boxShadow: '1px 0 0 #eef0f3, 0 1px 0 #eef0f3', backgroundColor: senNeed.senNeedData?.isActive ? '#eaf2fb' : 'transparent'}}
            >
                <Box component="label" sx={{display: 'flex', alignItems: 'center', gap: 1.5, cursor: readonly ? 'default' : 'pointer'}}>
                    <Checkbox
                        sx={{p: 0}}
                        checked={!!senNeed.senNeedData && senNeed.senNeedData.isActive}
                        onChange={event => updateSen({
                            isActive: event.target.checked,
                            name: senNeed.name,
                            senNeedId: senNeed.id,
                            id: senNeed.id
                        })}
                        disabled={readonly}
                    />
                    <Box sx={{minWidth: 0}}>
                        <Typography>{senNeed.name}</Typography>
                        {senNeed.isDisabled &&
                            <Typography color={'error'} variant={'body2'}>This Special Education Needs option is
                                no
                                longer available. Kindly deselect it, please.</Typography>}
                    </Box>
                </Box>
                {senNeed.senNeedData?.isActive && (
                    <TextField
                        multiline sx={{width: '100%', mt: 1, backgroundColor: '#fff'}} value={senNeed.senNeedData.note}
                        label={'Description'}
                        onChange={event =>
                            event.target.value.length < 255 && updateSen({
                                note: event.target.value,
                                id: senNeed.id
                            })}
                        disabled={readonly}
                    />
                )}
            </Box>
        ))}
        </Card>
    );

    const handleEhcpFileDelete = () => {
        setEhcpFile(null);
        if (senNeeds.ehcpFile?.id) {
            setEhcpFileDeleted(true);
        }
    };

    const handleDialogClose = () => {
        setOpenAddDocumentsDialog(false);
        setSelectedEhcpFile([]);
        setIsUploading(false);
        setIsUploadSizeExceedsLimit(false);
    };

    const handleEhcpFileUpload = async () => {
        setIsUploading(true);
        setEhcpFile(selectedEhcpFile[0]);
        setEhcpFileDeleted(false);
        handleDialogClose();
    };

    const handleEhcpFileSelected = (file) => {
        if (file[0]) {
            setSelectedEhcpFile([file[0]]);
        }
    };

    const handleEhcpFileDeleteFromUploadContext = () => {
        setSelectedEhcpFile([]);
    };

    const buildEhcpUploadFile =
        <Box mt={2}>
            <Typography>UPLOAD {selectedProfile.name?.toUpperCase()}’S EHCP (OPTIONAL)</Typography>
            <Box py={1} display={'flex'} flexDirection={{ xs: 'column', sm: 'row' }} justifyContent={'space-between'} key={`ehcp-${ehcpFile?.id}`}>
                <Box display="flex" alignItems="center" gap={1}>
                    {!readonly && (
                        <Button
                            sx={{
                                backgroundColor: '#186BBE',
                                boxShadow: 'none',
                                width: { xs: '100%', sm: 'auto' },
                                '&:hover': {
                                    backgroundColor: '#155FA7',
                                    boxShadow: 'none'
                                }
                            }}
                            variant="contained"
                            startIcon={<UploadFile/>}
                            onClick={() => setOpenAddDocumentsDialog(true)}
                        >
                            Upload current EHCP
                        </Button>
                    )}
                    {ehcpFile && (
                        <Box display={'flex'} alignItems="center">
                            <Tooltip title={ehcpFile.name} arrow>
                                <Typography
                                    noWrap
                                    sx={{
                                        maxWidth: {xs: '100px', sm: '200px', md: '430px'},
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {ehcpFile.name}
                                </Typography>
                            </Tooltip>
                            {ehcpFile.id && (
                                <IconButton
                                    onClick={async () =>
                                        await downloadFile(downloadProfileEhcpDocument, {
                                            documentId: ehcpFile.id,
                                            studentId,
                                        })
                                    }
                                >
                                    <DownloadIcon/>
                                </IconButton>
                            )}
                        </Box>
                    )}
                </Box>
                {!readonly && (
                    <Button
                        variant="outlined"
                        onClick={() => handleEhcpFileDelete()}
                        sx={{
                            width: '100px',
                            visibility: ehcpFile ? 'visible' : 'hidden',
                        }}
                    >
                        Delete
                    </Button>
                )}
            </Box>
        </Box>
    ;

    const isNothingChanged = () => {
        const isSenRowsInitial = senRows.filter(row => row.isEdited).length === 0;
        const isSenStatusInitial = senStatus === senNeeds.senStatus;
        const isSenNeedInvestigatingInitial = isSenInvestigatingUnchanged(
            senNeeds.senNeedInvestigating,
            senInvestigating
        );
        const isNewDocumentsClear = newDocuments.length === 0;
        const isDeletedDocumentsClear = deletedDocuments.length === 0;
        const isEhcpFileInitial = !ehcpFileDeleted &&
            !(ehcpFile instanceof File) &&
            ((!ehcpFile && !senNeeds.ehcpFile) ||
                (ehcpFile?.id === senNeeds.ehcpFile?.id && ehcpFile?.name === senNeeds.ehcpFile?.name));
        return isSenRowsInitial && isSenStatusInitial && isSenNeedInvestigatingInitial && isNewDocumentsClear && isDeletedDocumentsClear && isEhcpFileInitial;
    };

    const shouldEnableSaveButton = () => {
        switch (senStatus) {
            case SEN_STATUSES.N :
                return senNeeds.senStatus !== SEN_STATUSES.N;
            case SEN_STATUSES.K :
                return senRows.some(e => e.senNeedData?.isActive) && !isDisabledSenChecked() && !isAddMode
            case SEN_STATUSES.KS :
                return senRows.some(e => e.senNeedData?.isActive) && !isDisabledSenChecked() && !isAddMode
            case SEN_STATUSES.KI :
                return (senInvestigating.isChecked || (newDocuments.length > 0 || senDocumentRows?.length > 0)) && !isAddMode
            case SEN_STATUSES.E :
                return canConfirmEhcpSenStatus(ehcpFile);
            default :
                return false
        }
    };

    const renderNoDiagnosedOrSuspectedSEN = () => (
        <SenRow>
            <Typography sx={{color: senStatus && senStatus !== SEN_STATUSES.N ? '#9aa5b1' : 'inherit'}}>
                {selectedProfile.name} has no diagnosed or suspected Special Educational Needs or Disabilities that {currentSchool.name} need to be aware of.
            </Typography>
            <Checkbox
                sx={{width: '25px', height: '25px'}}
                checked={senStatus === SEN_STATUSES.N}
                onChange={e => chooseSenStatus(e.target.checked ? SEN_STATUSES.N : null)}
                disabled={readonly}
            />
        </SenRow>
    );

    const renderDiagnosedSEN = () => (
        <>
            <SenRow>
                <Typography sx={{color: senStatus && senStatus !== SEN_STATUSES.E ? '#9aa5b1' : 'inherit'}}>
                    {selectedProfile.name} has an Education, Health and Care Plan (EHCP) issued by {getHisHerPronoun(selectedProfile.gender)} Local Authority.
                </Typography>
                <Checkbox
                    sx={{width: '25px', height: '25px'}}
                    checked={senStatus === SEN_STATUSES.E}
                    onChange={e => chooseSenStatus(e.target.checked ? SEN_STATUSES.E : null)}
                    disabled={readonly}
                />
            </SenRow>
        </>
    );

    const renderDiagnosedBySchoolSEN = () => (
        <Box>
            <SenRow>
                {/* Maria, 18 October 2026: one answer for SEN identified by a professional or the current school.
                    A child saved with the old 'identified by school' answer (KS) shows this answer ticked and keeps KS
                    when saved, so the child stays in the 'SEN identified by school' group, the section stays confirmed
                    and nothing new is sent to SIMS. A child answering now is saved as K. */}
                <Typography sx={{color: senStatus && senStatus !== SEN_STATUSES.K && senStatus !== SEN_STATUSES.KS ? '#9aa5b1' : 'inherit'}}>
                    {selectedProfile.name} has Special Educational Needs that have been identified by a qualified professional
                    or {getHisHerPronoun(selectedProfile.gender)} current school. {capitalise(getHeShePronoun())} requires support at school but does not have an EHCP.
                </Typography>
                <Checkbox
                    sx={{width: '25px', height: '25px'}}
                    checked={senStatus === SEN_STATUSES.K || senStatus === SEN_STATUSES.KS}
                    onChange={event => chooseSenStatus(event.target.checked
                        ? (senNeeds.senStatus === SEN_STATUSES.KS ? SEN_STATUSES.KS : SEN_STATUSES.K)
                        : null)}
                    disabled={readonly}
                />
            </SenRow>
        </Box>
    );

    return <>
        {isSenNeedsLoading
            ? ''
            : <SenFields item xs={12} sm={6}>
                <ConfirmationWrapper
                    readonly={readonly}
                    confirmData={{
                        confirmMessage: `I confirm that the information in the special education needs profile section of ${selectedProfile.name} ${selectedProfile.surname}'s student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${selectedProfile.name} ${selectedProfile.surname} in their care on behalf of ${currentSchool.name}`,
                        confirmedAt: senNeeds.confirmedAt,
                        confirmedBy: senNeeds.confirmedBy,
                        enableSaveButton: !isNothingChanged() && shouldEnableSaveButton(),
                        // AD-2827: a parent with View access cannot change anything, so they can always confirm what is shown
                        enableConfirmButton: isViewOnlyParent || shouldEnableSaveButton() || (senNeeds.confirmedAt == null && senStatus === SEN_STATUSES.N),
                        studentId,
                        type: STUDENT_PROFILE_CONFIRMATION_TYPES.Sen,
                        handleSave,
                        onConfirmSuccess: async () => await refetchSenNeedsData(),
                        confirmButtonText: readonly ? 'Confirm' : 'Save',
                        confirmTooltip: isViewOnlyParent ? 'Please confirm that this information is correct.' : null
                    }}
                    // Maria, 18 October 2026: the save button at the bottom of the section
                    actionsBelow
                    additionalActions={isViewOnlyParent && (
                        <Button variant="outlined" onClick={() => setOpenUpdateRequest(true)}
                            sx={{width: {xs: '100%', sm: 'auto'}}}>{UPDATE_REQUEST_BUTTON}</Button>
                    )}
                    heading={isViewOnlyParent
                        ? (
                        <Typography sx={{flex: '1 1 360px', minWidth: 0, whiteSpace: 'pre-line'}}>
                            {formatProfileDescription(
                                senViewDescriptionData?.value ?? DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.senViewAccess],
                                {schoolName: currentSchool.name, studentName: selectedProfile.name, hisHer: getHisHerPronoun(selectedProfile.gender)}
                            )}
                        </Typography>
                    )
                        : (
                        <Typography fontWeight="bold" sx={{flex: '1 1 360px', minWidth: 0}}>Please tick one of the options below, so that we can
                            ensure {selectedProfile.name} receives the
                            appropriate level of Learning Support while at {currentSchool.name}. The information you
                            provide can be
                            updated at any time if a new assessment or diagnosis takes place, either before or
                            after {getHeShePronoun()} joins {currentSchool.name}</Typography>
                    )}
                >
                {actions => (<>
                    <Box marginTop={1} display="flex" flexDirection="column" gap={0} sx={{'& .MuiCheckbox-root': {height: '22px'}, ...(senStatus ? {'& .MuiCheckbox-root:not(.Mui-checked)': {color: '#9aa5b1'}} : {})}}>
                        {/* Maria, 18 October 2026: less space between the answers. All five answers stay visible whatever is ticked; the follow-up
                            for the ticked answer appears underneath them */}
                        {renderNoDiagnosedOrSuspectedSEN()}
                        {buildSenInInvestigatingRow}
                        {renderDiagnosedSEN()}
                        {renderDiagnosedBySchoolSEN()}
                    </Box>
                    <Box marginTop={1.5}>
                        {senStatus === SEN_STATUSES.E && buildEhcpUploadFile}
                        {(senStatus === SEN_STATUSES.K || senStatus === SEN_STATUSES.KS) && (
                            <Box>
                    <Typography py={1} sx={{whiteSpace: 'pre-line'}}>
                        {formatProfileDescription(
                            senDescription,
                            {
                                schoolName: currentSchool.name,
                                studentName: selectedProfile.name,
                                hisHer: getHisHerPronoun(selectedProfile.gender)
                            })
                        }
                    </Typography>
                    {buildSenRows()}
                    {buildSenDocuments(actions)}
                </Box>
                        )}
                        {senInvestigating?.isChecked && (
                            <Box>
                {/* AD-2827 (Maria, 30 October 2026): a parent with View access is not asked for a summary they cannot type;
                    the school's summary, if there is one, is shown as text */}
                {isViewOnlyParent
                    ? (senInvestigating.note && <Typography py={1} sx={{whiteSpace: 'pre-line'}}>{senInvestigating.note}</Typography>)
                    : <>
                <Typography py={1}>Please provide a short summary. If you would like to add a document to provide more
                        detailed information, please use the Add Document button.</Typography>
                    <TextField
                        multiline sx={{width: '100%'}} value={senInvestigating.note}
                        label={'Summary'}
                        onChange={event =>
                            event.target.value.length < 255 && handleChangeInInvestigatingComment(event.target.value)}
                        disabled={readonly}
                    />
                    </>}
                {buildSenDocuments(actions)}
            </Box>
                        )}
                    </Box>
                    <AdmicityDialog
                        handleClose={handleDialogClose}
                        title={'Add EHCP document'}
                        open={openAddDocumentsDialog}
                        actions={[
                            {label: 'Cancel', onClick: handleDialogClose},
                            {
                                label: 'Upload',
                                onClick: handleEhcpFileUpload,
                                disabled: isUploading || selectedEhcpFile?.length === 0 || isUploadSizeExceedsLimit,
                                startIcon: isUploading ? <CircularProgress color="inherit" size={16}/> : undefined
                            }
                        ]}
                        maxWidth={'md'}
                    >
                        <Uploader
                            totalUploadSizeInMb={25}
                            onFilesSelect={handleEhcpFileSelected}
                            onFileDelete={handleEhcpFileDeleteFromUploadContext}
                            onUploadLimitExceed={setIsUploadSizeExceedsLimit}
                            value={selectedEhcpFile}
                            readonly={isUploading}
                            signleFile={true}
                        />
                    </AdmicityDialog>
                    {/* AD-2827: once, under the documents, when 'being assessed' is ticked whatever the saved status */}
                    {!(senStatus === SEN_STATUSES.K || senStatus === SEN_STATUSES.KS || senStatus === SEN_STATUSES.KI || senInvestigating?.isChecked) && actions}
                </>)}
                </ConfirmationWrapper>
                {isViewOnlyParent && (
                    <UpdateRequestDialog
                        open={openUpdateRequest}
                        onClose={() => setOpenUpdateRequest(false)}
                        studentId={studentId}
                        section="Sen"
                        text={`Please tick the option that applies to ${selectedProfile.name} and add any comments. This information will be sent to ${currentSchool.name} so that they can update it.`}
                        options={[
                            `${selectedProfile.name} has no diagnosed or suspected Special Educational Needs or Disabilities that ${currentSchool.name} need to be aware of.`,
                            `${selectedProfile.name} is in the process of being assessed for Special Educational Needs and may require support at school depending on the outcome of the assessment.`,
                            `${selectedProfile.name} has an Education, Health and Care Plan (EHCP) issued by ${getHisHerPronoun(selectedProfile.gender)} Local Authority.`,
                            `${selectedProfile.name} has Special Educational Needs that have been identified by a qualified professional or ${getHisHerPronoun(selectedProfile.gender)} current school. ${capitalise(getHeShePronoun())} requires support at school but does not have an EHCP.`
                        ]}
                    />
                )}
            </SenFields>
        }
    </>;
};

SenAndDisabilities.propTypes = {
    readonly: PropTypes.bool
};
export default SenAndDisabilities;
